import { Button } from '@talentmesh/ui/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@talentmesh/ui/components/dialog';
import { Empty, EmptyDescription } from '@talentmesh/ui/components/empty';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import useActiveAtsImportJob from '../../api/useActiveAtsImportJob';
import useAtsImportJob, { isAtsImportFinished } from '../../api/useAtsImportJob';
import { AtsJobResponse } from '../../api/useAtsJobs';
import useImportAtsApplications from '../../api/useImportAtsApplications';
import AtsDialogLoading from './AtsDialogLoading';
import AtsJobPicker from './AtsJobPicker';
import AtsNotConnectedNotice from './AtsNotConnectedNotice';
import AtsSyncingNotice from './AtsSyncingNotice';
import useCompanyId from '@/Hooks/UseCompanyId';
import useRecruitmentId from '@/Hooks/useRecruitmentId';
import {
    isAtsSyncFinished,
    useAtsConnectionStatus,
} from '@/Pages/AccountSettings/AccountSettingsTabs/IntegrationsTab/api/useAtsIntegration';
import UIStrings from '@/Utils/UIStrings';

interface ImportFromAtsButtonProps {
    onImportCompleted: () => void;
}

/**
 * Entry point for pulling candidates out of the company's connected ATS.
 */
const ImportFromAtsButton = ({ onImportCompleted }: ImportFromAtsButtonProps) => {
    const companyId = useCompanyId();
    const recruitmentId = useRecruitmentId();
    const [open, setOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState<AtsJobResponse | null>(null);
    const [importJobId, setImportJobId] = useState<string | null>(null);

    const { data: connection, isLoading: isConnectionLoading, error: connectionError } = useAtsConnectionStatus(open);
    const { data: activeImportJob, isLoading: isCheckingForActiveImport } = useActiveAtsImportJob(recruitmentId);
    const { data: importJob } = useAtsImportJob(importJobId);
    const { trigger: importAtsApplications, isMutating: isStartingImport } = useImportAtsApplications(recruitmentId);

    const isConnected = !!connection;
    const isAtsSyncing = !!connection && !isAtsSyncFinished(connection);
    const hasFinished = importJobId !== null && isAtsImportFinished(importJob);
    const isImporting = importJobId !== null && !hasFinished;
    const canStartImport = !isCheckingForActiveImport && !isImporting;

    useEffect(() => {
        if (activeImportJob) {
            setImportJobId(activeImportJob.id);
        }
    }, [activeImportJob]);

    // Depends on hasFinished alone, deliberately. onImportCompleted reloads the page, so calling it
    // re-renders the header, which rebuilds the inline callback — with it in the dep array the effect
    // re-runs and calls it again, forever. useCallback cannot fix that: loadPageAsync is rebuilt by
    // CandidatesOverviewContext on every render.
    useEffect(() => {
        if (hasFinished) {
            onImportCompleted();
        }
    }, [hasFinished]);

    const handleClose = () => {
        setOpen(false);
        setSelectedJob(null);
    };

    const handleConfirm = async () => {
        if (!selectedJob) {
            return;
        }

        const importArsResponse = await importAtsApplications(selectedJob.id);

        // Rejected, and already reported by the hook. The dialog keeps the pick to retry or change.
        if (!importArsResponse) {
            return;
        }

        toast.success(UIStrings.AtsImportScheduled(importArsResponse.wouldImportCount));
        setImportJobId(importArsResponse.importJobId);
        handleClose();
    };

    const renderBody = () => {
        if (isConnectionLoading) {
            return <AtsDialogLoading />;
        }

        if (connectionError) {
            return (
                <Empty className="py-8" data-cy="AtsImportDialogError">
                    <div>
                        <EmptyDescription>{UIStrings.AtsJobsLoadFailed}</EmptyDescription>
                        <EmptyDescription>{UIStrings.AtsJobsLoadFailedRetryHint}</EmptyDescription>
                    </div>
                </Empty>
            );
        }

        if (!isConnected) {
            return <AtsNotConnectedNotice />;
        }

        if (isAtsSyncing) {
            return <AtsSyncingNotice />;
        }

        return <AtsJobPicker companyId={companyId} selectedJob={selectedJob} onSelectedJobChange={setSelectedJob} />;
    };

    // AtsNotConnectedNotice and AtsSyncingNotice bring their own footers, since their actions are
    // their own. The header goes with them too: "Select an ATS job" over a notice saying you cannot
    // reads as a broken dialog.
    const showHeader = isConnectionLoading || connectionError || (isConnected && !isAtsSyncing);
    const showPickerFooter = !isConnectionLoading && !connectionError && isConnected && !isAtsSyncing;

    return (
        <>
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                disabled={!canStartImport}
                data-cy="ImportFromAtsButton"
            >
                {isImporting ? <Spinner className="size-4" aria-hidden /> : <Download className="size-4" aria-hidden />}
                {isImporting ? UIStrings.ImportingFromAts : UIStrings.ImportFromAts}
            </Button>
            <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : handleClose())}>
                <DialogContent
                    className="flex max-h-[80vh] max-w-2xl flex-col overflow-hidden"
                    data-cy="ImportFromAtsDialog"
                >
                    {showHeader && (
                        <DialogHeader>
                            <DialogTitle className="text-xl font-semibold">{UIStrings.ImportFromAts}</DialogTitle>
                            <DialogDescription>{UIStrings.SelectAtsJobDescription}</DialogDescription>
                        </DialogHeader>
                    )}
                    {renderBody()}
                    {showPickerFooter && (
                        <DialogFooter className="items-center gap-2 sm:justify-end">
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={!selectedJob || isStartingImport}
                                    data-cy="AtsImportConfirm"
                                >
                                    {isStartingImport && <Spinner className="mr-2 size-4" />}
                                    {UIStrings.ImportFromAts}
                                </Button>
                            </div>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ImportFromAtsButton;
